import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Checkbox, Icon, Loader } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import { useSelector } from 'react-redux';
import { getCurrentOrganizationId } from '../../../common/selectors/auth';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from '../../../common/components/Table';
import { H2, PageHeader, PageSection } from '../../../common/styles/page';
import { StyledButton, StyledCancelButton } from '../../../common/components/button/StyledButton';
import { teamApi } from '../api/teamApi';

const ImportTeamReviewData: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToasts();

  const { results, file } = location.state || {};
  const organizationId = useSelector(getCurrentOrganizationId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [importTeams] = teamApi.useImportTeamsMutation();

  if (!results || !file) {
    return <p>Missing import data. Return to import step.</p>;
  }

  const headers = results.columns || [];
  const invalid_rows = results.invalid_rows || [];
  const verified_rows = results.verified_rows || [];
  const hasFlaggedRows = !results.all_valid;
  const hasImportErrors = Boolean(results.total_errors && results.total_errors > 0);
  const hasValidRows = verified_rows.length > 0;
  const importDisabled = isSubmitting;

  const getCellStatusProps = (status: string) => {
    switch (status) {
      case 'valid':
        return { positive: true };
      case 'warning':
        return { warning: true };
      case 'error':
        return { negative: true };
      default:
        return {};
    }
  };

  const proceedWithImport = async () => {
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await importTeams(formData as any).unwrap();

      addToast('Teams imported successfully', {
        appearance: 'success',
        autoDismiss: true,
      });

      navigate('/app/teams');
    } catch (err) {
      console.error('Import failed:', err);
      addToast('Failed to import teams', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageSection>
      <PageHeader
        title="Review Team Data"
        controls={
          <>
            <StyledCancelButton onClick={() => navigate(-1)} disabled={importDisabled} />
            <StyledButton
              onClick={proceedWithImport}
              disabled={importDisabled}
              label="Proceed with Team Import"
              color={hasImportErrors ? 'red' : 'green'}
            />
          </>
        }
      />

      <PageSection
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <PageSection display="flex" flexDirection="column">
          <p style={{ color: hasFlaggedRows ? 'red' : 'green', fontWeight: 'bold' }}>
            <Icon
              name={hasFlaggedRows ? 'warning circle' : 'check circle'}
              style={{ marginLeft: 6 }}
              color={hasFlaggedRows ? 'red' : 'green'}
            />
            {hasFlaggedRows
              ? `${results.total_warnings} warnings and ${results.total_errors} errors were found in the CSV import file.`
              : `${results.total_valid_rows} teams verified and ready for processing.`}
          </p>
          <p>Review the validated team import file and proceed to create or update teams in the organization.</p>
          {hasImportErrors && (
            <p style={{ color: 'darkorange', fontWeight: 600 }}>
              There are errors in the CSV, but you may still proceed. Rows with errors will not be imported.
            </p>
          )}
        </PageSection>
      </PageSection>

      {hasFlaggedRows && (
        <>
          <H2 padtop>Flagged Rows</H2>
          <Table celled compact>
            <TableHeader>
              <TableRow>
                <HeaderCell>Row</HeaderCell>
                {headers.map((col: any) => (
                  <HeaderCell key={col.name}>
                    {col.name.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase())}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {invalid_rows.map((row: any, rowIndex: number) => (
                <TableRow
                  key={rowIndex}
                  warning={row.row_status === 'warning'}
                  negative={row.row_status === 'error'}
                >
                  <TableCell>{row.row_number}</TableCell>
                  {headers.map((col: any) => {
                    const cell = row.fields?.[col.name];
                    const status = cell?.status || 'valid';
                    const cellProps = getCellStatusProps(status);
                    return (
                      <TableCell key={col.name} {...cellProps} title={cell?.error || ''}>
                        {status !== 'valid' && (
                          <Icon
                            name="warning circle"
                            style={{ marginLeft: 6 }}
                            color={status === 'error' ? 'red' : 'yellow'}
                          />
                        )}
                        {cell?.value || ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {hasValidRows && (
        <>
          <H2 padtop>Verified Rows</H2>
          <Table celled compact>
            <TableHeader>
              <TableRow>
                <HeaderCell>Row</HeaderCell>
                {headers.map((col: any) => (
                  <HeaderCell key={col.name}>
                    {col.name.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase())}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {verified_rows.map((row: any, rowIndex: number) => (
                <TableRow
                  key={rowIndex}
                  warning={row.row_status === 'warning'}
                  negative={row.row_status === 'error'}
                >
                  <TableCell>{row.row_number}</TableCell>
                  {headers.map((col: any) => {
                    const cell = row.fields?.[col.name];
                    const status = cell?.status || 'valid';
                    const cellProps = getCellStatusProps(status);
                    return (
                      <TableCell key={col.name} {...cellProps} title={cell?.error || ''}>
                        {status !== 'valid' && (
                          <Icon
                            name="warning circle"
                            style={{ marginLeft: 6 }}
                            color={status === 'error' ? 'red' : 'yellow'}
                          />
                        )}
                        {cell?.value || ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </PageSection>
  );
};

export default ImportTeamReviewData;
